import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { Crown, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1 = enter email, 2 = enter token + password
  const [resetLoading, setResetLoading] = useState(false);

  const isNL = i18n.language === 'nl';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password, rememberMe);
      toast.success(isNL ? 'Succesvol ingelogd!' : 'Successfully logged in!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || (isNL ? 'Inloggen mislukt' : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
      setResetStep(2);
      toast.success(isNL ? 'Reset code verzonden! Check je email.' : 'Reset code sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isNL ? 'Kon reset niet versturen' : 'Could not send reset'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success(isNL ? 'Wachtwoord succesvol gereset! Je kunt nu inloggen.' : 'Password reset! You can now login.');
      setShowForgotPassword(false);
      setResetStep(1);
      setResetEmail('');
      setResetToken('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isNL ? 'Reset mislukt' : 'Reset failed'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://customer-assets.emergentagent.com/job_model-subscription-1/artifacts/gewvo18c_F9971100-1D75-4017-9230-91F17493651E.jpeg"
          alt="Model"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo with Glamour */}
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Crown className="w-8 h-8 text-[#D4AF37] drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]" />
            <span className="text-2xl">
              <span className="brand-ella text-white">Ella</span>{' '}
              <span className="brand-gold text-3xl">Gold</span>
            </span>
          </Link>

          {/* Header */}
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight mb-3">
            {t('auth.welcomeBack').split(' ')[0]} <span className="brand-gold text-4xl">{t('auth.welcomeBack').split(' ').slice(1).join(' ') || 'back'}</span>
          </h1>
          <p className="text-gray-400 mb-8">
            Login to access your exclusive content
          </p>

          {/* Form */}
          {!showForgotPassword ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input 
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@email.com"
                      className="pl-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white placeholder:text-gray-600"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input 
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white placeholder:text-gray-600"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="remember-me"
                    />
                    <span className="text-sm text-gray-400">
                      {isNL ? 'Onthoud mij (30 dagen)' : 'Remember me (30 days)'}
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#D4AF37] hover:text-[#FCD34D] transition-colors"
                  >
                    {isNL ? 'Wachtwoord vergeten?' : 'Forgot password?'}
                  </Link>
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gold py-6 text-lg"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      {isNL ? 'Inloggen...' : 'Logging in...'}
                    </span>
                  ) : (
                    t('auth.login')
                  )}
                </Button>
              </form>

              {/* Register Link */}
              <p className="mt-8 text-center text-gray-400">
                {t('auth.noAccount')}{' '}
                <Link 
                  to="/register" 
                  className="text-[#D4AF37] hover:text-[#FCD34D] transition-colors"
                  data-testid="register-link"
                >
                  {t('auth.register')}
                </Link>
              </p>
            </>
          ) : (
            /* Forgot Password Form */
            <div className="space-y-6">
              <button
                onClick={() => { setShowForgotPassword(false); setResetStep(1); }}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                {isNL ? 'Terug naar inloggen' : 'Back to login'}
              </button>

              <h2 className="text-2xl font-medium">
                {isNL ? 'Wachtwoord resetten' : 'Reset Password'}
              </h2>

              {resetStep === 1 ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-gray-400 text-sm">
                    {isNL 
                      ? 'Voer je email in om een reset code te ontvangen.' 
                      : 'Enter your email to receive a reset code.'}
                  </p>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input 
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="name@email.com"
                        className="pl-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white"
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    disabled={resetLoading}
                    className="w-full btn-gold"
                  >
                    {resetLoading 
                      ? (isNL ? 'Verzenden...' : 'Sending...') 
                      : (isNL ? 'Reset code versturen' : 'Send reset code')}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <p className="text-gray-400 text-sm">
                    {isNL 
                      ? 'Voer de reset code en je nieuwe wachtwoord in.' 
                      : 'Enter the reset code and your new password.'}
                  </p>
                  <div className="space-y-2">
                    <Label className="text-gray-300">{isNL ? 'Reset Code' : 'Reset Code'}</Label>
                    <Input 
                      type="text"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] text-white font-mono text-sm"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">{isNL ? 'Nieuw wachtwoord' : 'New password'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input 
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 bg-white/5 border-white/10 focus:border-[#D4AF37] text-white"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    disabled={resetLoading}
                    className="w-full btn-gold"
                  >
                    {resetLoading 
                      ? (isNL ? 'Resetten...' : 'Resetting...') 
                      : (isNL ? 'Wachtwoord resetten' : 'Reset password')}
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
